import React, { useRef, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import {
  DashboardLayout,
  DashboardWidgetId,
  DashboardWidgetProps,
  DashboardWidthSpan,
  getDashboardWidget,
} from '../config/dashboardWidgets';
import { buildDashboardLayoutRows } from '../config/dashboardWidgets/layoutRows';
import { COLORS } from '../constants/theme';
import { DashboardWidthSpanToggle } from './DashboardWidthSpanToggle';
import { DashboardWidgetDragOverlay, widgetIdFromPoint } from './DashboardWidgetDragOverlay';

interface DashboardLayoutRendererProps {
  readonly layout: DashboardLayout;
  readonly widgetProps: DashboardWidgetProps;
  readonly forceSingleColumn?: boolean;
  readonly isPreview?: boolean;
  readonly onSwapWidgets?: (sourceId: DashboardWidgetId, targetId: DashboardWidgetId) => void;
  readonly onSetWidthSpan?: (widgetId: DashboardWidgetId, widthSpan: DashboardWidthSpan) => void;
}

export const DashboardLayoutRenderer: React.FC<DashboardLayoutRendererProps> = ({
  layout,
  widgetProps,
  forceSingleColumn = false,
  isPreview = false,
  onSwapWidgets,
  onSetWidthSpan,
}) => {
  const rows = buildDashboardLayoutRows(layout, { forceSingleColumn });
  const orderedIds = rows.flatMap((row) => row.widgets.map((widget) => widget.id));
  const allowDrag = Boolean(isPreview && onSwapWidgets);
  const allowWidthSpanControls = Boolean(
    isPreview && onSetWidthSpan && layout.columnCount === 2 && !forceSingleColumn
  );
  const draggingIdRef = useRef<DashboardWidgetId | null>(null);
  const [draggingId, setDraggingId] = useState<DashboardWidgetId | null>(null);
  const [dropTargetId, setDropTargetId] = useState<DashboardWidgetId | null>(null);

  const handlePointerDown = (widgetId: DashboardWidgetId) => {
    draggingIdRef.current = widgetId;
    setDraggingId(widgetId);
    setDropTargetId(null);
  };

  const handlePointerMove = (event: { clientX: number; clientY: number }) => {
    const sourceId = draggingIdRef.current;
    if (!sourceId) {
      return;
    }

    const overId = widgetIdFromPoint(event.clientX, event.clientY);
    const nextTarget = overId && overId !== sourceId ? overId : null;
    setDropTargetId((current) => (current === nextTarget ? current : nextTarget));
  };

  const handlePointerUp = (event: { clientX: number; clientY: number }) => {
    const sourceId = draggingIdRef.current;
    const targetId = widgetIdFromPoint(event.clientX, event.clientY);
    draggingIdRef.current = null;
    setDraggingId(null);
    setDropTargetId(null);

    if (!sourceId || !targetId || sourceId === targetId || !onSwapWidgets) {
      return;
    }

    onSwapWidgets(sourceId, targetId);
  };

  return (
    <View style={[styles.grid, isPreview && styles.previewGrid]}>
      {isPreview ? (
        <Text style={styles.dragHint}>
          Drag a widget onto another slot. The grid stays in place; only the content moves.
        </Text>
      ) : null}
      {rows.map((row) => (
        <View
          key={row.widgets.map((widget) => widget.id).join('-')}
          style={row.span === 'half' ? styles.halfRow : styles.fullRow}
        >
          {row.widgets.map((placement) => {
            const definition = getDashboardWidget(placement.id);
            if (!definition) {
              return null;
            }

            const WidgetComponent = definition.Component;
            const isHalf = row.span === 'half';
            const isAloneHalf = isHalf && row.widgets.length === 1;
            const orderIndex = orderedIds.indexOf(placement.id);
            const isDropTarget = dropTargetId === placement.id && draggingId !== placement.id;

            return (
              <View
                key={placement.id}
                style={[
                  isAloneHalf ? styles.halfSlotAlone : isHalf ? styles.halfSlot : styles.fullSlot,
                  isDropTarget && styles.dropTargetSlot,
                ]}
              >
                {isPreview ? (
                  <View style={styles.reorderBar}>
                    <Text style={styles.reorderIndex}>{orderIndex + 1}</Text>
                    {allowWidthSpanControls && onSetWidthSpan ? (
                      <DashboardWidthSpanToggle
                        widgetTitle={definition.title}
                        widthSpan={placement.widthSpan}
                        onChange={(widthSpan) => onSetWidthSpan(placement.id, widthSpan)}
                      />
                    ) : (
                      <Text style={styles.dragHandleLabel}>Drag the chart to move</Text>
                    )}
                  </View>
                ) : null}
                <DashboardWidgetDragOverlay
                  widgetId={placement.id}
                  enabled={allowDrag}
                  isDragging={draggingId === placement.id}
                  onPointerDown={handlePointerDown}
                  onPointerMove={handlePointerMove}
                  onPointerUp={handlePointerUp}
                >
                  <View style={draggingId === placement.id ? styles.draggingSlot : undefined}>
                    <WidgetComponent {...widgetProps} />
                  </View>
                </DashboardWidgetDragOverlay>
              </View>
            );
          })}
        </View>
      ))}
    </View>
  );
};

const styles = StyleSheet.create({
  grid: {
    flexDirection: 'column',
    gap: 20,
    width: '100%',
  },
  previewGrid: {
    padding: 8,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#BFD4DC',
    backgroundColor: 'rgba(0,80,110,0.03)',
  },
  dragHint: {
    fontSize: 13,
    color: COLORS.textSecondary,
    fontFamily: 'Montserrat_400Regular',
    marginBottom: 4,
  },
  halfRow: {
    flexDirection: 'row',
    flexWrap: 'nowrap',
    gap: 20,
    width: '100%',
  },
  fullRow: {
    flexDirection: 'column',
    width: '100%',
  },
  halfSlot: {
    flex: 1,
    minWidth: 0,
  },
  halfSlotAlone: {
    width: '50%',
    minWidth: 0,
  },
  fullSlot: {
    width: '100%',
  },
  draggingSlot: {
    opacity: 0.55,
  },
  dropTargetSlot: {
    borderRadius: 10,
    borderWidth: 2,
    borderColor: COLORS.primary,
  },
  reorderBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
    paddingHorizontal: 4,
    zIndex: 2,
  },
  reorderIndex: {
    fontSize: 13,
    fontWeight: '700',
    color: COLORS.primary,
    fontFamily: 'Montserrat_700Bold',
  },
  dragHandleLabel: {
    fontSize: 12,
    color: COLORS.textSecondary,
    fontFamily: 'Montserrat_500Medium',
  },
});
